import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Sparkles,
  ShieldCheck,
  TrendingUp,
  Clock,
  Timer,
  ChevronLeft,
  ChevronRight,
  Mail,
  FileText,
  PenLine,
  ArrowRight,
  Lightbulb,
  Check,
  X,
  Loader2,
  Filter,
  CalendarDays,
} from 'lucide-react';

const CATEGORY_MAP = {
  financieel: 'Financieel & Belasting',
  contracten: 'Contracten & Huur',
  leveranciers: 'Leveranciers & Inkoop',
  techniek: 'Techniek & Apparatuur',
  voertuigen: 'Voertuigen',
  vastgoed: 'Vastgoed & Facilitair',
  veiligheid: 'Veiligheid & Compliance',
  milieu: 'Milieu & Infrastructuur',
  nieuwbouw: 'Nieuwbouw & Verbouwing',
  overnames: 'Overnames',
  automatisering: 'Automatisering',
};

const SOURCE_ICON = {
  email: Mail,
  manual: PenLine,
  document: FileText,
};

const SOURCE_LABEL = {
  email: 'E-mail',
  manual: 'Handmatig',
  document: 'Document',
};

const SOURCE_STYLE = {
  email: 'bg-blue-50 text-blue-700 border-blue-200',
  manual: 'bg-slate-50 text-slate-600 border-slate-200',
  document: 'bg-purple-50 text-purple-700 border-purple-200',
};

function ConfidenceBar({ pct }) {
  const color = pct >= 80 ? 'bg-green-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500';
  const textColor = pct >= 80 ? 'text-green-700' : pct >= 60 ? 'text-amber-700' : 'text-red-700';
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-[11px] font-medium ${textColor}`}>{pct}%</span>
    </div>
  );
}

function KPICard({ icon: Icon, label, value, sub, color = '#2E75B6', testId }) {
  return (
    <div data-testid={testId} className="bg-white rounded-xl border border-slate-200/80 p-4 flex items-center gap-3.5 min-w-0">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${color}12` }}>
        <Icon className="w-5 h-5" style={{ color }} />
      </div>
      <div className="min-w-0">
        <p className="text-lg font-bold text-slate-900 leading-tight">{value}</p>
        <p className="text-[11px] text-slate-500 truncate">{label}</p>
        {sub && <p className="text-[10px] text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function SuggestionItem({ suggestion, onStatusChange, updating }) {
  const TYPE_STYLE = {
    action: 'bg-blue-50 text-blue-600 border-blue-200',
    warning: 'bg-amber-50 text-amber-600 border-amber-200',
    optimization: 'bg-green-50 text-green-600 border-green-200',
    cross_reference: 'bg-purple-50 text-purple-600 border-purple-200',
  };

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 border border-slate-100 group">
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-700 leading-relaxed">{suggestion.text}</p>
        <div className="flex items-center gap-2 mt-1.5">
          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${TYPE_STYLE[suggestion.type] || TYPE_STYLE.action}`}>
            {suggestion.type}
          </span>
          <span className="text-[10px] text-slate-400 truncate">{suggestion.entry_title}</span>
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        {suggestion.status === 'pending' && (
          <>
            <button
              onClick={() => onStatusChange(suggestion, 'acted')}
              disabled={updating}
              className="p-1 rounded text-green-600 hover:bg-green-50 transition-colors"
              title="Afgehandeld"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => onStatusChange(suggestion, 'dismissed')}
              disabled={updating}
              className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors"
              title="Afwijzen"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default function AiNormalisatie() {
  const { authAxios } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [suggestions, setSuggestions] = useState({ grouped: { pending: [], acted: [], dismissed: [] }, total: 0 });
  const [loading, setLoading] = useState(true);
  const [activityLoading, setActivityLoading] = useState(false);
  const [updatingSuggestion, setUpdatingSuggestion] = useState(false);

  // Filters
  const [dateRange, setDateRange] = useState('alles');
  const [sourceFilter, setSourceFilter] = useState('alle');
  const [confFilter, setConfFilter] = useState('alle');
  const [page, setPage] = useState(1);

  // Suggestion tab
  const [sugTab, setSugTab] = useState('pending');

  const loadStats = useCallback(async () => {
    try {
      const api = authAxios();
      const res = await api.get('/ai/normalize/stats');
      setStats(res.data);
    } catch (e) {
      console.error('Stats load failed:', e);
    }
  }, [authAxios]);

  const loadActivity = useCallback(async (p = 1) => {
    setActivityLoading(true);
    try {
      const api = authAxios();
      const params = new URLSearchParams();
      params.set('page', p);
      params.set('limit', 20);
      if (dateRange !== 'alles') params.set('date_range', dateRange);
      if (sourceFilter !== 'alle') params.set('source', sourceFilter);
      if (confFilter !== 'alle') params.set('min_confidence', confFilter);
      const res = await api.get(`/ai/normalize/activity?${params}`);
      setActivity(res.data);
    } catch (e) {
      console.error('Activity load failed:', e);
    } finally {
      setActivityLoading(false);
    }
  }, [authAxios, dateRange, sourceFilter, confFilter]);

  const loadSuggestions = useCallback(async () => {
    try {
      const api = authAxios();
      const res = await api.get('/ai/normalize/suggestions');
      setSuggestions(res.data);
    } catch (e) {
      console.error('Suggestions load failed:', e);
    }
  }, [authAxios]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([loadStats(), loadActivity(1), loadSuggestions()]);
      setLoading(false);
    };
    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setPage(1);
    loadActivity(1);
  }, [dateRange, sourceFilter, confFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handlePageChange = (p) => {
    setPage(p);
    loadActivity(p);
  };

  const handleSuggestionStatus = async (suggestion, newStatus) => {
    setUpdatingSuggestion(true);
    try {
      const api = authAxios();
      await api.put(`/ai/normalize/suggestions/${suggestion.entry_id}/${suggestion.suggestion_id}?status=${newStatus}`);
      await loadSuggestions();
    } catch (e) {
      console.error('Suggestion status update failed:', e);
    } finally {
      setUpdatingSuggestion(false);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      const now = new Date();
      const diffMs = now - d;
      const diffH = Math.floor(diffMs / 3600000);
      if (diffH < 1) return 'Zojuist';
      if (diffH < 24) return `${diffH}u geleden`;
      const diffD = Math.floor(diffH / 24);
      if (diffD < 7) return `${diffD}d geleden`;
      return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
    } catch { return ''; }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {[1,2,3,4,5].map(i => <div key={i} className="h-20 bg-slate-100 rounded-xl" />)}
        </div>
        <div className="h-96 bg-slate-100 rounded-xl" />
      </div>
    );
  }

  const timeSavedHours = stats ? (stats.time_saved_minutes / 60).toFixed(1) : '0';

  return (
    <div data-testid="ai-normalisatie-page" className="space-y-6">
      {/* KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="kpi-strip">
        <KPICard
          icon={Sparkles}
          label="Genormaliseerd"
          value={stats?.total_normalized || 0}
          testId="kpi-normalized"
          color="#2E75B6"
        />
        <KPICard
          icon={ShieldCheck}
          label="Compliance gedetecteerd"
          value={stats?.total_compliance_detected || 0}
          testId="kpi-compliance"
          color="#16a34a"
        />
        <KPICard
          icon={TrendingUp}
          label="Gem. confidence"
          value={`${stats?.avg_confidence || 0}%`}
          testId="kpi-confidence"
          color={stats?.avg_confidence >= 80 ? '#16a34a' : stats?.avg_confidence >= 60 ? '#d97706' : '#dc2626'}
        />
        <KPICard
          icon={Clock}
          label="Vandaag verwerkt"
          value={stats?.today_count || 0}
          testId="kpi-today"
          color="#8b5cf6"
        />
        <KPICard
          icon={Timer}
          label="Tijdsbesparing"
          value={`${timeSavedHours} uur`}
          sub="bespaard"
          testId="kpi-time-saved"
          color="#0891b2"
        />
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Activity Feed (main) */}
        <div className="flex-1 min-w-0">
          {/* Filters */}
          <div className="flex items-center flex-wrap gap-2 mb-4" data-testid="activity-filters">
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <Filter className="w-3.5 h-3.5" />
            </div>
            <select
              data-testid="filter-date"
              value={dateRange}
              onChange={e => setDateRange(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/30"
            >
              <option value="alles">Alle periodes</option>
              <option value="vandaag">Vandaag</option>
              <option value="7dagen">7 dagen</option>
              <option value="30dagen">30 dagen</option>
            </select>
            <select
              data-testid="filter-source"
              value={sourceFilter}
              onChange={e => setSourceFilter(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/30"
            >
              <option value="alle">Alle bronnen</option>
              <option value="email">E-mail</option>
              <option value="manual">Handmatig</option>
              <option value="document">Document</option>
            </select>
            <select
              data-testid="filter-confidence"
              value={confFilter}
              onChange={e => setConfFilter(e.target.value)}
              className="px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-[#2E75B6]/30"
            >
              <option value="alle">Alle confidence</option>
              <option value="hoog">Hoog (&gt;80%)</option>
              <option value="gemiddeld">Gemiddeld (60-80%)</option>
              <option value="laag">Laag (&lt;60%)</option>
            </select>
            <span className="text-[11px] text-slate-400 ml-auto">
              {activity.total} resultaten
            </span>
          </div>

          {/* Activity List */}
          <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden" data-testid="activity-feed">
            {activityLoading ? (
              <div className="p-8 text-center">
                <Loader2 className="w-5 h-5 text-[#2E75B6] animate-spin mx-auto" />
              </div>
            ) : activity.items.length === 0 ? (
              <div className="p-12 text-center">
                <Sparkles className="w-8 h-8 text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-500">Nog geen genormaliseerde items</p>
                <p className="text-xs text-slate-400 mt-1">Gebruik AI Normalisatie bij het aanmaken van nieuwe items</p>
              </div>
            ) : (
              <div>
                {activity.items.map((item) => {
                  const SourceIcon = SOURCE_ICON[item.source] || PenLine;
                  const titleChanged = item.original_title && item.original_title !== item.title;
                  return (
                    <div
                      key={item.entry_id}
                      data-testid={`activity-row-${item.entry_id}`}
                      onClick={() => navigate(`/kennisbank/${item.entry_id}`)}
                      className="flex items-center gap-3 px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50/70 cursor-pointer transition-colors group"
                    >
                      {/* Timestamp */}
                      <span className="text-[11px] text-slate-400 w-16 flex-shrink-0 text-right">
                        {formatTime(item.created_at)}
                      </span>

                      {/* Title with diff */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          {titleChanged && (
                            <>
                              <span className="text-xs text-slate-400 line-through truncate max-w-[200px]">{item.original_title}</span>
                              <ArrowRight className="w-3 h-3 text-slate-300 flex-shrink-0" />
                            </>
                          )}
                          <span className="text-sm font-medium text-slate-800 truncate group-hover:text-[#2E75B6] transition-colors">
                            {item.title}
                          </span>
                        </div>
                        {/* Chips row */}
                        <div className="flex items-center flex-wrap gap-1.5">
                          {/* Source badge */}
                          <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${SOURCE_STYLE[item.source] || SOURCE_STYLE.manual}`}>
                            <SourceIcon className="w-2.5 h-2.5" />
                            {SOURCE_LABEL[item.source] || item.source}
                          </span>
                          {/* Location */}
                          {item.location_id && item.location_id !== 'all' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-200 text-slate-500 bg-slate-50">
                              {item.location_id}
                            </span>
                          )}
                          {/* Category */}
                          {item.category && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded border border-[#2E75B6]/20 text-[#2E75B6] bg-[#2E75B6]/5">
                              {CATEGORY_MAP[item.category] || item.category}
                            </span>
                          )}
                          {/* Compliance detected */}
                          {item.compliance_detected > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded border border-green-200 text-green-700 bg-green-50 font-medium">
                              +{item.compliance_detected} compliance
                            </span>
                          )}
                          {/* Suggestions */}
                          {item.suggestions_count > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded border border-amber-200 text-amber-700 bg-amber-50">
                              {item.suggestions_count} suggesties
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Confidence bar */}
                      <div className="flex-shrink-0 w-24 hidden sm:block">
                        <ConfidenceBar pct={item.confidence_pct} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {activity.pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50/50">
                <span className="text-[11px] text-slate-400">
                  Pagina {page} van {activity.pages}
                </span>
                <div className="flex items-center gap-1">
                  <button
                    data-testid="activity-prev"
                    onClick={() => handlePageChange(Math.max(1, page - 1))}
                    disabled={page <= 1}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 disabled:opacity-30 transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    data-testid="activity-next"
                    onClick={() => handlePageChange(Math.min(activity.pages, page + 1))}
                    disabled={page >= activity.pages}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 disabled:opacity-30 transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Suggestions Overview (right column) */}
        <div className="lg:w-80 flex-shrink-0">
          <div className="bg-white rounded-xl border border-slate-200/80 overflow-hidden" data-testid="suggestions-overview">
            <div className="px-4 py-3 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-800 flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-500" />
                Suggesties
                <span className="text-[10px] text-slate-400 font-normal">({suggestions.total})</span>
              </h3>
            </div>

            {/* Tabs */}
            <div className="flex border-b border-slate-100">
              {[
                { key: 'pending', label: 'Open', count: suggestions.grouped?.pending?.length || 0 },
                { key: 'acted', label: 'Afgehandeld', count: suggestions.grouped?.acted?.length || 0 },
                { key: 'dismissed', label: 'Afgewezen', count: suggestions.grouped?.dismissed?.length || 0 },
              ].map(tab => (
                <button
                  key={tab.key}
                  data-testid={`sug-tab-${tab.key}`}
                  onClick={() => setSugTab(tab.key)}
                  className={`flex-1 px-3 py-2 text-[11px] font-medium transition-colors ${
                    sugTab === tab.key
                      ? 'text-[#2E75B6] border-b-2 border-[#2E75B6]'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>

            {/* Suggestion items */}
            <div className="p-3 space-y-2 max-h-[500px] overflow-y-auto">
              {(suggestions.grouped?.[sugTab] || []).length === 0 ? (
                <p className="text-xs text-slate-400 text-center py-4">Geen suggesties</p>
              ) : (
                (suggestions.grouped?.[sugTab] || []).map((sug, idx) => (
                  <SuggestionItem
                    key={`${sug.entry_id}-${sug.suggestion_id || idx}`}
                    suggestion={sug}
                    onStatusChange={handleSuggestionStatus}
                    updating={updatingSuggestion}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
